import { Dropdown, SelectOption } from "components/dropdown/Dropdown";
import { useField, useFormikContext } from "formik";
import { SetStateAction, useEffect, useState } from "react";
import {
  ParsedWorkflowItem,
  ReferenceData,
  WorkflowBuilderErrors,
  WorkflowReferenceDataNames,
} from "../WorkflowBuilderPage";
import { MultiDropdown } from "components/dropdown/MultiDropdown";
import _ from "lodash";
import {
  changeWorkflowBuilderReferenceData,
  updateWorkflowBuilderValue,
} from "./WorkflowItem";

import editIcon from "assets/edit_icon.png";

interface Props {
  fieldName: string;
  documentOptions: SelectOption[];
  projectOptions: SelectOption[];
  type: "trigger" | "action";
  triggerData: any;
  parsedData: ParsedWorkflowItem | null;
  setParsedData: React.Dispatch<SetStateAction<ParsedWorkflowItem | null>>;
  referenceData: ReferenceData | null;
  setReferenceData: React.Dispatch<SetStateAction<ReferenceData | null>>;
  showTriggerTypeSelector: boolean;
  setShowTriggerTypeSelector: React.Dispatch<SetStateAction<boolean>>;
  workflowBuilderErrors: WorkflowBuilderErrors;
  contactTypes: SelectOption[];
}

export type EditNodeProps = {
  setShowSelector: React.Dispatch<SetStateAction<any>>;
};

export const EditNode: React.FC<EditNodeProps> = ({ setShowSelector }) => {
  return (
    <div className="EditNode" onClick={() => setShowSelector(true)}>
      <img src={editIcon} alt="Edit type" className="EditNodeIcon" />
    </div>
  );
};

const NewCommunicationTrigger: React.FC<Props> = ({
  fieldName,
  documentOptions,
  projectOptions,
  type,
  triggerData,
  parsedData,
  setParsedData,
  referenceData,
  setReferenceData,
  showTriggerTypeSelector,
  setShowTriggerTypeSelector,
  workflowBuilderErrors,
  contactTypes,
}) => {
  const [relevantErrors, setRelevantErrors] = useState<string[] | null>(null);

  useEffect(() => {
    if (
      referenceData &&
      referenceData.trigger &&
      (!referenceData.trigger.hasOwnProperty("bApplyToAllProjects") ||
        (referenceData.trigger.hasOwnProperty("bApplyToAllProjects") &&
          referenceData.trigger === null))
    ) {
      let newReferenceData = _.cloneDeep(referenceData);

      if (
        referenceData.trigger.projectTypesIDs &&
        Array.isArray(referenceData.trigger.projectTypesIDs) &&
        referenceData.trigger.projectTypesIDs.length > 0
      ) {
        newReferenceData.trigger.bApplyToAllProjects = false;
        handleBooleanChange("bApplyToAllProjects", false);
      } else {
        newReferenceData.trigger.bApplyToAllProjects = true;
        handleBooleanChange("bApplyToAllProjects", true);
      }

      setReferenceData(newReferenceData);
    }
  }, []);

  useEffect(() => {
    if (
      workflowBuilderErrors &&
      workflowBuilderErrors.trigger &&
      workflowBuilderErrors.trigger.hasOwnProperty(0)
    ) {
      const tempErrors = workflowBuilderErrors.trigger[0];

      if (Array.isArray(tempErrors) && tempErrors.length > 0) {
        setRelevantErrors(tempErrors);
      } else {
        setRelevantErrors(null);
      }
    } else {
      setRelevantErrors(null);
    }
  }, [workflowBuilderErrors]);

  const handleChangeDocument = (option: SelectOption) => {
    if (parsedData) {
      let newParsedData: any = _.cloneDeep(parsedData);

      const newValue = option ? option.value : null;

      newParsedData.documentTypesID = newValue;

      setParsedData(newParsedData);

      let newReferenceData = _.cloneDeep(referenceData);

      if (newReferenceData?.trigger) {
        newReferenceData.trigger.documentTypesID = newValue;
      }

      setReferenceData(newReferenceData);
    }
  };

  const handleChangeProject = (options: SelectOption[]) => {
    if (parsedData) {
      let newParsedData: any = _.cloneDeep(parsedData);

      const newValue = options ? options.map(item => item.value) : null;

      newParsedData.projectTypesIDs = newValue;

      let newApplyToAllProjectsValue = false;

      if (newValue) {
        newParsedData.bApplyToAllProjects = false;
        newApplyToAllProjectsValue = false;
      } else {
        newParsedData.bApplyToAllProjects = true;
        newApplyToAllProjectsValue = true;
      }

      setParsedData(newParsedData);

      let newReferenceData = _.cloneDeep(referenceData);

      if (newReferenceData?.trigger) {
        newReferenceData.trigger.projectTypesIDs = newValue;
        newReferenceData.trigger.bApplyToAllProjects = newApplyToAllProjectsValue;
      }

      setReferenceData(newReferenceData);
    }
  };

  const handleBooleanChange = (fieldName: string, finalValue?: boolean) => {
    if (parsedData) {
      let newParsedData: any = {
        ...parsedData,
      };

      const newValue =
        finalValue !== undefined
          ? finalValue
          : newParsedData && newParsedData.hasOwnProperty(fieldName)
          ? !newParsedData[fieldName]
          : newParsedData && !newParsedData.hasOwnProperty(fieldName)
          ? true
          : false;

      newParsedData[fieldName] = newValue;

      setParsedData(newParsedData);

      let newReferenceData = _.cloneDeep(referenceData);

      if (newReferenceData?.trigger) {
        newReferenceData.trigger[fieldName] = newValue;
      }

      setReferenceData(newReferenceData);
    }
  };

  const handleSave = () => {};

  const handleDropdownChangeMulti = (
    options: SelectOption[],
    fieldName: string
  ) => {
    const optionIds = options ? options.map(item => item.value) : null;

    const newParsedData = updateWorkflowBuilderValue(
      parsedData,
      "0",
      fieldName,
      optionIds,
      "replace"
    );

    setParsedData(newParsedData);

    let newReferenceData = _.cloneDeep(referenceData);

    if (newReferenceData?.trigger) {
      newReferenceData.trigger[fieldName] = optionIds;
    }

    setReferenceData(newReferenceData);
  };

  const title = "New Communication";
  return (
    <div
      className={`NewCommunicationTrigger TriggerContainer DetailsItemContent ${
        relevantErrors && relevantErrors.length > 0 ? "Error" : ""
      }`}
    >
      <label className="Title">
        <div className="Text">{`Trigger - ${title}`}</div>

        <EditNode setShowSelector={setShowTriggerTypeSelector} />
      </label>

      <div className="Row">
        <div className="Label">
          Project type
          {/* <span className="Italic"> - opt</span> */}
        </div>

        <MultiDropdown
          customClassName="MultiDropdown"
          onChange={handleChangeProject}
          value={
            triggerData &&
            triggerData.projectTypesIDs &&
            Array.isArray(triggerData.projectTypesIDs)
              ? projectOptions.filter(o =>
                  triggerData.projectTypesIDs.includes(o.value)
                )
              : null
          }
          options={projectOptions}
          placeholder="Please select"
          isClearable={true}
          roundedRectangle
          portalMode={true}
        />

        <div className="ExtraInformation">
          (If you don't select any projects it will apply to all projects)
        </div>
      </div>

      <div className="Row">
        <div className="Label">Contact types</div>

        <MultiDropdown
          customClassName="MultiDropdown"
          onChange={(e: any) => handleDropdownChangeMulti(e, "contactTypesIDs")}
          value={
            contactTypes &&
            Array.isArray(contactTypes) &&
            triggerData &&
            triggerData.contactTypesIDs &&
            Array.isArray(triggerData.contactTypesIDs)
              ? contactTypes.filter(o =>
                  triggerData.contactTypesIDs.includes(o.value)
                )
              : null
          }
          options={contactTypes}
          placeholder="Please select"
          isClearable={false}
          roundedRectangle
          portalMode={true}
        />
      </div>

      {/* <div className="Row">
        <div className="Label">Apply to all projects</div>

        <div
          className="Radio"
          role="radio"
          aria-checked={triggerData && triggerData.bApplyToAllProjects}
        >
          <input
            id={"bApplyToAllProjectsCheckbox"}
            type="radio"
            name={"bApplyToAllProjectsCheckbox"}
            value="bMessageText"
            onClick={() => handleBooleanChange("bApplyToAllProjects")}
            checked={triggerData && triggerData.bApplyToAllProjects}
          />
          <label
            className="CustomInput"
            htmlFor={"bApplyToAllProjectsCheckbox"}
          ></label>
        </div>
      </div> */}

      {relevantErrors && relevantErrors.length > 0 && (
        <div className="Errors">
          {relevantErrors.map(error => {
            return <div className="Error">{error}</div>;
          })}
        </div>
      )}

      {/* <button type="button" className="SaveBtn" onClick={handleSave}>
        Save
      </button> */}
    </div>
  );
};

export default NewCommunicationTrigger;
